import { SitesType as StargateSitesType } from '@atlassiansox/asc-stargate';
import type { AnalyticsTrackType } from '@atlassiansox/bxpkit-core';
import { ProductKey } from '@atlassiansox/bxpkit-core';

/**
 * userInfo
 */
export type LabelsType = { [key: string]: string };

export type SitesType = StargateSitesType[] | null;

export type UserDataType = {
  readonly isLoggedIn: boolean;
  readonly email: string | null;
  readonly fullName: string | null;
  readonly firstName: string | undefined;
  readonly sites: SitesType;
  readonly isNewUser?: boolean | null;
  readonly lastName: string | null;
  readonly extendedTrialOverridableSites: string[] | null;
  readonly picture: string | null;
  readonly accountId: string | null;
};

export type MigrationDataType = {
  readonly migrationSourceUuid: string;
  readonly migrationUserLimit: number;
  readonly migrationUserType: string;
  readonly migrationProductName: string;
  readonly migrationSites: string[];
  readonly invalidMigration: boolean;
  readonly claimedMigration: boolean;
};

export type ProductKeysType = {
  product: string;
  edition: string;
  imgSource: string | undefined;
  serverDownloadContent: string | undefined;
  productContent: ProductType[] | undefined;
};

/**
 * Payload Types
 */

export type ProductPayloadType = {
  readonly accountName: string;
  readonly product: ProductKey;
  readonly edition?: string;
};

export type ProductPayloadActivateType = {
  readonly accountName: string;
  readonly productKey: ProductKey;
  readonly edition?: string;
};

/**
 * Types for root-component props
 */

type LayoutType =
  | 'default'
  | 'standalone'
  | 'standalone-v2'
  | 'devops'
  | 'devops-v2'
  | 'devops-v2-standalone'
  | 'first-impressions'
  | 'first-impressions-v2'
  | 'simple-signup';

export type ProductType = {
  readonly productKey: string;
  readonly productName: string;
  readonly productLogoUrl?: string;
  readonly productLogoWhiteUrl?: string;
};

export type ProductDataType = {
  readonly products: ProductType[];
  readonly bundleProductKeys: string;
  readonly iconSrc?: string;
  readonly iconAlt?: string;
  readonly calloutRichText?: string;
};

export type LocaleType = {
  readonly locale: string;
  readonly localeRequiresMarketingCommunicationOptIn: boolean;
};

/**
 * WacSignupLayoutType
 *
 * @param allowEnableAutogenSiteName - enable/disable autogen site names
 * @param allowSignupWithGoogle - enable/disable Signup with Google
 * @param enableFreeBreachTest - enable/disable Free Breach (Test)
 * @param showSocialSignupForm - enable/disable social signup
 * @param socialContinueUrl - callback/continue URL for when user has completed social signing in
 * @param layout - type of layout to use (eg. 'standalone', 'default')
 * @param productOverride - supports the same values as the `product` query param, but overrides any parameters (or lack of param)
 * @param labels - Localized labels from provided externally (eg. Magnolia)
 * @param productData - Data (provided externally, eg. from Magnolia) on all existing products/bundles
 */

export type WacSignupLayoutType = {
  readonly allowEnableAutogenSiteName: boolean;
  readonly allowSignupWithGoogle: boolean;
  readonly enableFreeBreachTest: boolean;
  readonly showSocialSignupForm: boolean;
  readonly socialContinueUrl: string;
  readonly loadingImageSrc: string;
  readonly errorImageSrc: string;
  readonly productData: { [key: string]: ProductDataType };
  readonly locale: LocaleType;
  readonly analyticsTrack: AnalyticsTrackType;
  readonly labels: LabelsType;
  readonly layout?: LayoutType;
  readonly productOverride?: string;
  readonly performanceStartTime?: number;
  readonly siteRenamePageUrl?: string;
  readonly enableSkipRecaptcha?: boolean;
};

/**
 * Component Types
 */

export type SideInfoOptionsType = {
  readonly labels: LabelsType;
  readonly productKeys: ProductKeysType[];
  readonly userInfo?: UserDataType;
  readonly formExistingSiteShown: boolean;
  readonly isMigrationSignup?: boolean;
  readonly migrationData?: MigrationDataType;
  readonly analyticsTrack: AnalyticsTrackType;
};

export type SignupOptionsType = {
  readonly productKeys: ProductKeysType[];
  readonly userInfo?: UserDataType;
  readonly locale: LocaleType;
  readonly analyticsTrack: AnalyticsTrackType;
  readonly enableFreeBreachTest: boolean;
  readonly socialContinueUrl: string;
  readonly allowEnableAutogenSiteName: boolean;
  readonly setFormExistingSiteShown: (val: boolean) => void;
  readonly isMigrationSignup?: boolean;
  readonly migrationData?: MigrationDataType;
  readonly allowSignupWithGoogle?: boolean;
  readonly enableFieldProgression?: boolean;
  readonly fadeOnDisable?: boolean;
  readonly popOverError?: boolean;
  readonly outlineOnFocus?: boolean;
  readonly showWarningIcon?: boolean;
  readonly hideCaptchaEula?: boolean;
  readonly isSuiteSignup?: boolean;
  readonly enableResendEmail?: boolean;
  readonly enableCarouselEndCard?: boolean;
  readonly siteRenamePageUrl: string;
  readonly enableSkipRecaptcha?: boolean;
  readonly hideGreeting?: boolean;
};

export type SocialSignonButtonOptionsType = {
  readonly analyticsTrack: AnalyticsTrackType;
  readonly socialContinueUrl: string;
  readonly isEmailSignupSelected?: boolean;
  readonly setIsEmailSignupSelected?: (val: boolean) => void;
};

export type SocialSignupOptionsType = {
  readonly locale: LocaleType;
  readonly userInfo?: UserDataType;
  readonly productKeys: ProductKeysType[];
  readonly analyticsTrack: AnalyticsTrackType;
  readonly loadingImageSrc: string;
  readonly errorImageSrc: string;
  readonly showSocialSignupForm: boolean;
  readonly children?: React.ReactNode;
  readonly siteRenamePageUrl: string;
};

export type HeaderDevopsOptionsType = {
  readonly labels: LabelsType;
  readonly productKeys: ProductKeysType[];
  readonly formExistingSiteShown: boolean;
  readonly analyticsTrack: AnalyticsTrackType;
  readonly userInfo?: UserDataType;
};

export type SideInfoDevopsOptionsType = {
  readonly labels: LabelsType;
  readonly productKeys: ProductKeysType[];
  readonly formExistingSiteShown: boolean;
  readonly analyticsTrack: AnalyticsTrackType;
};

/**
 * Layout
 */

export type ContainerLayoutType = SideInfoOptionsType &
  SignupOptionsType &
  SocialSignupOptionsType &
  SocialSignonButtonOptionsType &
  HeaderDevopsOptionsType &
  SideInfoDevopsOptionsType;

export type DevopsImageUrlsType = {
  readonly sidebarImage: string;
  readonly backgroundImage: string;
  readonly envelopeN2N: string;
  readonly successN2E: string;
  readonly codeAwsJsw: string;
};

/**
 * Devops N2N form
 *
 */
export type FormDataN2N = {
  readonly email: string;
  readonly sitenameNew: string;
  readonly fullName: string;
  readonly password: string;
  readonly marketingConsent?: boolean;
};

export type FormDataN2E = {
  readonly cloudName?: string;
  readonly productsNew?: ProductPayloadType[];
  readonly productsExisting?: ProductKey[];
  readonly marketingConsent?: boolean;
  readonly bbWorkspaceName?: string;
};

export type ValidationStateType =
  | 'validating'
  | 'error'
  | 'success'
  | undefined;

export type N2NFieldsType = {
  readonly email: boolean;
  readonly password: boolean;
  readonly fullName: boolean;
  readonly sitenameNew: boolean;
};
export type N2NDeferredPasswordFieldsType = {
  readonly email: boolean;
  readonly fullName: boolean;
};
export type AuthenticationMethod = {
  readonly type: AuthenticationMethodsType;
  readonly redirect_to?: string;
};
export enum AuthenticationMethodsType {
  PASSWORD = 'password',
  GOOGLE = 'google',
  SAML = 'saml',
  MICROSOFT = 'microsoft',
  APPLE = 'apple',
  SLACK = 'slack'
}
